import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RealListWizard from "@/components/reader/RealListWizard";
import PricingRules from "@/components/reader/PricingRules";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { MaterialsPriceList } from "@/components/price_lists/MaterialsPriceList";
import { Copy, History, Pencil, Power, Calculator, Download } from "lucide-react";

const UNITS = [
  ["EUR_PER_MIN", "€/minuto"], ["EUR_PER_HOUR", "€/ora"],
  ["EUR_PER_PIECE", "€/pezzo"], ["EUR_PER_LOT", "€/lotto"],
  ["EUR_PER_METER", "€/metro"], ["EUR_PER_M2", "€/m²"],
  ["EUR_PER_KG", "€/kg"], ["EUR_PER_HOLE", "€/foro"],
  ["EUR_PER_GEOMETRIC_BEND", "€/piega geometrica"],
  ["EUR_PER_PRESS_OPERATION", "€/operazione pressa"],
  ["FIXED_PRICE", "Prezzo fisso"],
  ["EXTERNAL_QUOTE", "Preventivo esterno/manuale"],
];
const unitLabel = (u) => (UNITS.find(([k]) => k === u) || [u, u])[1];
const CATEGORIES = ["TAGLIO", "FORATURA/FILETTATURA", "ASPORTAZIONE", "PIEGATURA",
  "CALANDRATURA", "SALDATURA", "FINITURA", "TRATTAMENTI", "MONTAGGIO",
  "QUALITÀ", "LOGISTICA", "ESTERNO", "ALTRO"];

const emptyItem = {
  code: "", name: "", category: "TAGLIO", description: "",
  machine_or_department: "", currency: "EUR", calc_unit: "EUR_PER_MIN",
  internal_cost: "", sell_mode: "MARKUP", sell_price: "", markup_pct: "",
  setup_cost: "", min_charge: "", setup_time_min: "",
  condition_material: "", condition_thickness: "", valid_from: "",
  notes: "", active: true,
};

function NumField({ label, field, form, setForm, testId, step = "0.01" }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" min="0" step={step} data-testid={testId}
        value={form[field] ?? ""}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })} />
    </div>
  );
}

function ItemDialog({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(emptyItem);
  useEffect(() => {
    setForm(editing ? { ...emptyItem, ...Object.fromEntries(
      Object.entries(editing).map(([k, v]) => [k, v ?? ""]) ),
      active: editing.active !== false } : emptyItem);
  }, [editing, open]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/price-lists/items/${editing.item_key}`, form);
        toast.success("Nuova versione creata");
      } else {
        await api.post("/price-lists/items", form);
        toast.success("Lavorazione creata");
      }
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="opaque-editable-fields max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle data-testid="item-dialog-title">
          {editing ? `Modifica ${editing.code} (crea versione ${editing.version + 1})` : "Nuova lavorazione"}
        </DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3 text-sm">
          <div><Label className="text-xs">Codice *</Label>
            <Input data-testid="item-code" value={form.code} required
              onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
          <div><Label className="text-xs">Nome *</Label>
            <Input data-testid="item-name" value={form.name} required
              onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label className="text-xs">Categoria (libera)</Label>
            <Input data-testid="item-category" list="pl-categories" value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <datalist id="pl-categories">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist></div>
          <div><Label className="text-xs">Macchina / reparto (opz.)</Label>
            <Input data-testid="item-machine" value={form.machine_or_department}
              onChange={(e) => setForm({ ...form, machine_or_department: e.target.value })} /></div>
          <div className="col-span-2"><Label className="text-xs">Descrizione</Label>
            <Input data-testid="item-description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div><Label className="text-xs">Unità di calcolo</Label>
            <Select value={form.calc_unit} onValueChange={(v) => setForm({ ...form, calc_unit: v })}>
              <SelectTrigger data-testid="item-calc-unit"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Piega geometrica ≠ operazione pressa: per un HEM valorizzare pre-piega e chiusura come operazioni pressa.
            </p></div>
          <div><Label className="text-xs">Valuta</Label>
            <Input data-testid="item-currency" value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
          <NumField label="Costo interno (per unità)" field="internal_cost" form={form} setForm={setForm} testId="item-internal-cost" />
          <div><Label className="text-xs">Modalità di vendita</Label>
            <Select value={form.sell_mode || "MARKUP"} onValueChange={(v) => setForm({ ...form, sell_mode: v })}>
              <SelectTrigger data-testid="item-sell-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DIRECT">Prezzo di vendita diretto</SelectItem>
                <SelectItem value="MARKUP">Ricarico % sul costo</SelectItem>
              </SelectContent>
            </Select></div>
          {form.sell_mode === "DIRECT"
            ? <NumField label="Prezzo di vendita (per unità)" field="sell_price" form={form} setForm={setForm} testId="item-sell-price" />
            : <NumField label="Ricarico %" field="markup_pct" form={form} setForm={setForm} testId="item-markup" step="0.1" />}
          <NumField label="Costo attrezzaggio/setup (€)" field="setup_cost" form={form} setForm={setForm} testId="item-setup-cost" />
          <NumField label="Addebito minimo (€)" field="min_charge" form={form} setForm={setForm} testId="item-min-charge" />
          <NumField label="Tempo setup (min, opz.)" field="setup_time_min" form={form} setForm={setForm} testId="item-setup-time" step="1" />
          <div><Label className="text-xs">Condizione materiale (opz.)</Label>
            <Input data-testid="item-cond-material" placeholder="es. AISI 304" value={form.condition_material}
              onChange={(e) => setForm({ ...form, condition_material: e.target.value })} /></div>
          <div><Label className="text-xs">Condizione spessore (opz.)</Label>
            <Input data-testid="item-cond-thickness" placeholder="es. 1–3 mm" value={form.condition_thickness}
              onChange={(e) => setForm({ ...form, condition_thickness: e.target.value })} /></div>
          <div><Label className="text-xs">Valido dal</Label>
            <Input type="date" data-testid="item-valid-from" value={form.valid_from}
              onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></div>
          <div className="flex items-center gap-2 pt-5">
            <Switch data-testid="item-active" checked={form.active !== false}
              onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <span className="text-xs">{form.active !== false ? "Attiva" : "Inattiva"}</span></div>
          <div className="col-span-2"><Label className="text-xs">Note</Label>
            <Textarea rows={2} data-testid="item-notes" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" data-testid="item-save-btn">
              {editing ? "Salva nuova versione" : "Crea"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VersionsDialog({ itemKey, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (itemKey) api.get(`/price-lists/items/${itemKey}/versions`).then(({ data }) => setRows(data));
  }, [itemKey]);
  return (
    <Dialog open={!!itemKey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Storico versioni</DialogTitle></DialogHeader>
        <div className="text-xs max-h-[60vh] overflow-y-auto" data-testid="versions-list">
          {(rows || []).map((v) => (
            <div key={v.id} className={`border-b border-border py-2 grid grid-cols-6 gap-2 ${v.is_current ? "font-medium" : "text-muted-foreground"}`}>
              <div>v{v.version}{v.is_current ? " (corrente)" : ""}</div>
              <div className="col-span-2">{v.code} — {v.name}</div>
              <div>{v.internal_cost != null ? fmtMoney(v.internal_cost) : "—"}</div>
              <div>{v.sell_mode === "DIRECT" ? (v.sell_price != null ? fmtMoney(v.sell_price) : "—") : (v.markup_pct != null ? `+${v.markup_pct}%` : "—")}</div>
              <div>{v.active === false ? "Inattiva" : "Attiva"} · {String(v.created_at || "").slice(0, 10)}</div>
            </div>
          ))}
          {rows && rows.length === 0 && <div className="py-4 text-center">Nessuna versione.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviewCard() {
  const [p, setP] = useState({ calc_unit: "EUR_PER_MIN", quantity: "10", amount_per_piece: "2",
    internal_cost: "1.5", sell_mode: "MARKUP", sell_price: "", markup_pct: "35",
    setup_cost: "30", min_charge: "80" });
  const [res, setRes] = useState(null);
  const run = async () => {
    try {
      const body = Object.fromEntries(Object.entries(p).map(([k, v]) =>
        [k, ["calc_unit", "sell_mode"].includes(k) ? v : (v === "" ? null : parseFloat(v))]));
      const { data } = await api.post("/price-lists/preview", body);
      setRes(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="border border-border bg-white p-4" data-testid="preview-card">
      <div className="flex items-center gap-2 mb-1">
        <Calculator size={15} />
        <span className="font-display font-bold text-sm">Anteprima di calcolo</span>
      </div>
      <div className="text-[10px] bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1 mb-3" data-testid="preview-synthetic-banner">
        Dati di ESEMPIO SINTETICO — nessun valore viene salvato come prezzo reale.
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="col-span-2"><Label className="text-xs">Unità</Label>
          <Select value={p.calc_unit} onValueChange={(v) => setP({ ...p, calc_unit: v })}>
            <SelectTrigger data-testid="preview-unit"><SelectValue /></SelectTrigger>
            <SelectContent>{UNITS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
        {[["quantity", "Quantità pezzi"], ["amount_per_piece", "Quantità unità/pezzo"],
          ["internal_cost", "Costo interno"], ["setup_cost", "Setup €"], ["min_charge", "Minimo €"],
          [p.sell_mode === "DIRECT" ? "sell_price" : "markup_pct", p.sell_mode === "DIRECT" ? "Prezzo diretto" : "Ricarico %"]]
          .map(([f, l]) => (
            <div key={f}><Label className="text-xs">{l}</Label>
              <Input type="number" step="0.01" data-testid={`preview-${f}`} value={p[f]}
                onChange={(e) => setP({ ...p, [f]: e.target.value })} /></div>))}
        <div className="col-span-2"><Label className="text-xs">Vendita</Label>
          <Select value={p.sell_mode} onValueChange={(v) => setP({ ...p, sell_mode: v })}>
            <SelectTrigger data-testid="preview-sell-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="MARKUP">Ricarico %</SelectItem>
              <SelectItem value="DIRECT">Prezzo diretto</SelectItem>
            </SelectContent>
          </Select></div>
      </div>
      <Button size="sm" className="mt-3 w-full" onClick={run} data-testid="preview-run-btn">Calcola esempio</Button>
      {res && (
        <div className="mt-3 text-xs space-y-1 border-t border-border pt-2" data-testid="preview-result">
          {res.missing && <div className="text-destructive">Valori mancanti: {res.missing.join(", ")} (nessun numero inventato)</div>}
          {res.requires_manual_quote && <div>{res.note}</div>}
          {res.total != null && (<>
            <div className="flex justify-between"><span>Prezzo unitario</span><span data-testid="preview-unit-price">{fmtMoney(res.unit_price)}</span></div>
            <div className="flex justify-between"><span>Lavoro</span><span>{fmtMoney(res.work_total)}</span></div>
            <div className="flex justify-between"><span>Setup</span><span>{fmtMoney(res.setup_cost)}</span></div>
            <div className="flex justify-between font-medium"><span>Totale{res.min_charge_applied ? " (minimo applicato)" : ""}</span>
              <span data-testid="preview-total">{fmtMoney(res.total)}</span></div>
          </>)}
        </div>
      )}
    </div>
  );
}

export default function PriceLists() {
  const { user } = useAuth();
  const canEdit = ["owner", "admin"].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "materials" ? "materials" : "operations";
  const [items, setItems] = useState([]);
  const [showInactive, setShowInactive] = useState(true);
  const [filter, setFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [versionsFor, setVersionsFor] = useState(null);

  const load = async () => {
    const { data } = await api.get("/price-lists/items?include_inactive=true");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const importCatalog = async () => {
    try {
      const { data } = await api.post("/price-lists/import-catalog");
      toast.success(`Catalogo base: ${data.created} lavorazioni create, ${data.skipped} già presenti`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggle = async (it) => {
    try { await api.post(`/price-lists/items/${it.item_key}/toggle`);
      toast.success(it.active === false ? "Attivata" : "Disattivata (mai cancellata)"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const duplicate = async (it) => {
    try { await api.post(`/price-lists/items/${it.item_key}/duplicate`);
      toast.success("Duplicata"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const visible = useMemo(() => items
    .filter((i) => showInactive || i.active !== false)
    .filter((i) => !filter || `${i.code} ${i.name} ${i.category}`.toLowerCase().includes(filter.toLowerCase())),
    [items, showInactive, filter]);

  return (
    <div className="opaque-editable-fields p-8" data-testid="price-lists-page">
      {canEdit && <RealListWizard />}
      {canEdit && <PricingRules />}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Impostazioni</div>
          <h1 className="text-3xl font-display font-bold mt-1">Listini e costi</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Prezzi per azienda (multi-tenant). Ogni modifica crea una nuova versione: i preventivi futuri
            useranno uno snapshot immutabile. {!canEdit && "— Accesso in sola lettura."}
          </p>
        </div>
        {canEdit && tab === "operations" && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={importCatalog} data-testid="btn-import-catalog">
              <Download size={14} className="mr-2" />Importa catalogo base</Button>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="btn-new-item">
              + Lavorazione</Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={(t) => setSearchParams({ tab: t })}>
        <TabsList className="mb-4 w-fit">
          <TabsTrigger value="operations" data-testid="tab-operations">Lavorazioni</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-price-materials">Materiali</TabsTrigger>
        </TabsList>
        <TabsContent value="materials"><MaterialsPriceList /></TabsContent>
        <TabsContent value="operations">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className="flex items-center gap-3 mb-3">
            <Input placeholder="Filtra per codice, nome, categoria…" className="max-w-xs"
              data-testid="filter-input" value={filter} onChange={(e) => setFilter(e.target.value)} />
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="toggle-show-inactive" />
              Mostra inattive
            </label>
            <span className="text-xs text-muted-foreground ml-auto" data-testid="items-count">{visible.length} lavorazioni</span>
          </div>
          <div className="border border-border bg-white overflow-x-auto">
            <table className="w-full text-xs" data-testid="price-list-table">
              <thead><tr className="border-b border-border bg-muted text-left">
                {["Codice", "Nome", "Categoria", "Unità", "Costo int.", "Vendita", "Setup", "Minimo", "Ver.", "Stato", ""]
                  .map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {visible.map((it) => (
                  <tr key={it.id} className={`border-b border-border ${it.active === false ? "opacity-50" : ""}`} data-testid={`row-${it.code}`}>
                    <td className="px-3 py-2 font-mono">{it.code}</td>
                    <td className="px-3 py-2">{it.name}</td>
                    <td className="px-3 py-2">{it.category}</td>
                    <td className="px-3 py-2">{unitLabel(it.calc_unit)}</td>
                    <td className="px-3 py-2">{it.internal_cost != null ? fmtMoney(it.internal_cost, it.currency) : "—"}</td>
                    <td className="px-3 py-2">{it.sell_mode === "DIRECT"
                      ? (it.sell_price != null ? fmtMoney(it.sell_price, it.currency) : "—")
                      : (it.markup_pct != null ? `costo +${it.markup_pct}%` : "—")}</td>
                    <td className="px-3 py-2">{it.setup_cost != null ? fmtMoney(it.setup_cost, it.currency) : "—"}</td>
                    <td className="px-3 py-2">{it.min_charge != null ? fmtMoney(it.min_charge, it.currency) : "—"}</td>
                    <td className="px-3 py-2">v{it.version}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 text-[10px] ${it.active === false ? "bg-secondary" : "bg-emerald-100 text-emerald-800"}`}>
                        {it.active === false ? "INATTIVA" : "ATTIVA"}</span></td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1.5 justify-end">
                        <button title="Versioni" onClick={() => setVersionsFor(it.item_key)} data-testid={`versions-${it.code}`}
                          className="p-1 hover:bg-muted border border-border"><History size={12} /></button>
                        {canEdit && (<>
                          <button title="Modifica (nuova versione)" onClick={() => { setEditing(it); setDialogOpen(true); }}
                            data-testid={`edit-${it.code}`} className="p-1 hover:bg-muted border border-border"><Pencil size={12} /></button>
                          <button title="Duplica" onClick={() => duplicate(it)} data-testid={`dup-${it.code}`}
                            className="p-1 hover:bg-muted border border-border"><Copy size={12} /></button>
                          <button title={it.active === false ? "Attiva" : "Disattiva"} onClick={() => toggle(it)}
                            data-testid={`toggle-${it.code}`} className="p-1 hover:bg-muted border border-border"><Power size={12} /></button>
                        </>)}
                      </div></td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground" data-testid="empty-state">
                    Nessuna lavorazione. {canEdit ? "Usa \u201CImporta catalogo base\u201D per iniziare senza prezzi precompilati." : ""}
                  </td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
        <div><PreviewCard /></div>
      </div>
        </TabsContent>
      </Tabs>

      <ItemDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} onSaved={load} />
      <VersionsDialog itemKey={versionsFor} onClose={() => setVersionsFor(null)} />
    </div>
  );
}
